// src/services/maps/config.js
// Single source of truth for every map configuration value.
// Tile providers, overlay definitions, default viewport, zoom limits.
// Nothing here instantiates Leaflet — pure data, importable anywhere.

// ── Viewport ──────────────────────────────────────────────────────
export const MAP_DEFAULTS = {
  center:      [29.9511, -90.0715],  // New Orleans city center
  zoom:        12,
  minZoom:     9,
  maxZoom:     18,
  zoomControl: false,                // we render our own
};


// ── Overlays ──────────────────────────────────────────────────────
// Overlays are rendered on top of the base tile layer.
// zIndex determines stacking order (higher = on top).
export const OVERLAYS = {
  noaa_radar: {
    id:          'noaa_radar',
    label:       'NOAA Radar',
    type:        'tile',
    url:         'https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png',
    attribution: '© <a href="https://mesonet.agron.iastate.edu/">IEM</a>',
    opacity:     0.65,
    maxZoom:     16,
    zIndex:      200,
    refreshMs:   5 * 60_000,   // radar tiles refresh every ~5 min
    offlineOk:   false,
  },
  fema_flood: {
    id:          'fema_flood',
    label:       'FEMA Flood Zones',
    type:        'tile',
    url:         'https://hazards.fema.gov/gis/nfhl/services/public/NFHL/MapServer/tile/{z}/{y}/{x}',
    attribution: '© <a href="https://msc.fema.gov/">FEMA NFHL</a>',
    opacity:     0.45,
    maxZoom:     18,
    zIndex:      150,
    offlineOk:   false,
  },
  nws_warnings: {
    id:          'nws_warnings',
    label:       'NWS Warnings',
    type:        'tile',
    url:         'https://idpgis.ncep.noaa.gov/arcgis/rest/services/NWS_Forecasts_Guidance_Warnings/watch_warn_adv/MapServer/tile/{z}/{y}/{x}',
    attribution: '© NOAA/NWS',
    opacity:     0.5,
    maxZoom:     12,
    zIndex:      180,
    offlineOk:   false,
  },
  usgs_gauges: {
    id:          'usgs_gauges',
    label:       'Stream Gauges',
    type:        'geojson',   // rendered as markers, not tiles
    zIndex:      300,
    offlineOk:   true,
  },
};

// ── Zoom thresholds ───────────────────────────────────────────────
// Controls what appears at each zoom level to avoid overload.

// ── Vector tile providers ─────────────────────────────────────────
// Vector tiles load faster, scale crisply, and support custom styling.
// They require a MapTiler/Mapbox key — set VITE_MAPTILER_KEY in .env.
//
// Raster (OSM) is the free default. Vector activates with a key.
// During disasters/low power, vector is preferred:
//   - Smaller files (~60% of raster size at equivalent detail)
//   - Renders on GPU, not CPU
//   - Supports simplified geometry at low zoom
//
// Note: Leaflet.VectorGrid must be installed for GL-based vector tiles.
// For simplicity we use the MapTiler raster-style endpoint which serves
// pre-rendered raster tiles optimized for mobile.
const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY ?? '';

export const TILE_PROVIDERS = {
  // Default: OpenStreetMap raster — free, no key required
  osm: {
    id:        'osm',
    label:     'Street',
    url:       'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:'© OpenStreetMap contributors',
    maxZoom:    19,
    vector:     false,
  },
  // MapTiler streets (raster, mobile-optimized) — requires key
  maptiler: {
    id:        'maptiler',
    label:     'Detailed',
    url:       MAPTILER_KEY
      ? `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`
      : null,   // disabled without key
    attribution:'© MapTiler © OpenStreetMap',
    maxZoom:    22,
    vector:     false,
    notes:      'Requires VITE_MAPTILER_KEY — set in .env.local',
  },
  // Satellite hybrid — only available with MapTiler key
  satellite: {
    id:        'satellite',
    label:     'Satellite',
    url:       MAPTILER_KEY
      ? `https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`
      : null,
    attribution:'© MapTiler © OpenStreetMap',
    maxZoom:    22,
    vector:     false,
    notes:      'Disabled in lite map mode — data-intensive',
    disableInLiteMode: true,
  },
};

// Filter to available providers (no key = osm only)
export const AVAILABLE_PROVIDERS = Object.values(TILE_PROVIDERS).filter(p => p.url);
export const DEFAULT_PROVIDER    = TILE_PROVIDERS.osm;
export const ZOOM_THRESHOLDS = {
  SHOW_CLUSTERS:      13,   // below this zoom, markers are clustered
  SHOW_LABELS:        14,   // below this, marker labels are hidden
  SHOW_FLOOD_DETAIL:  12,   // below this, FEMA tiles are too coarse to show
  TILE_CACHE_MIN:     10,   // minimum zoom level to cache offline
  TILE_CACHE_MAX:     13,   // maximum zoom level to cache offline
};

// ── Cluster configuration ─────────────────────────────────────────
export const CLUSTER_CONFIG = {
  maxClusterRadius: 60,        // px — max distance to merge markers
  spiderfyOnMaxZoom: true,
  showCoverageOnHover: false,
  zoomToBoundsOnClick: true,
  // Priority: SOS clusters render in red, others in amber
  iconCreateFunction: null,    // set in leafletCluster.js where Leaflet is available
};

// ── Map mode definitions ──────────────────────────────────────────
export const MAP_MODES = [
  {
    id:          'situation',
    label:       'Situation',
    icon:        '🗺',
    desc:        'Active incidents and community reports',
    layers:      [],                         // no overlays — just markers
    defaultZoom: 12,
  },
  {
    id:          'flood',
    label:       'Flood Zones',
    icon:        '🌊',
    desc:        'FEMA flood zones with live USGS gauges',
    layers:      ['fema_flood', 'usgs_gauges'],
    defaultZoom: 11,
  },
  {
    id:          'radar',
    label:       'Radar',
    icon:        '🌧',
    desc:        'NOAA live precipitation radar',
    layers:      ['noaa_radar', 'nws_warnings'],
    defaultZoom: 9,
  },
  {
    id:          'shelters',
    label:       'Shelters',
    icon:        '🏠',
    desc:        'Open emergency shelters and cooling centers',
    layers:      [],
    defaultZoom: 11,
  },
  {
    id:          'gas',
    label:       'Gas',
    icon:        '⛽',
    desc:        'Open gas stations — community reported',
    layers:      [],
    defaultZoom: 12,
  },
  {
    id:          'evac',
    label:       'Evacuation',
    icon:        '🚗',
    desc:        'Evacuation routes and contraflow status',
    layers:      [],
    defaultZoom: 10,
  },
  {
    id:          'roads',
    label:       'Road Closures',
    icon:        '🚗',
    desc:        'DOTD road closures and incidents',
    layers:      [],
    defaultZoom: 11,
  },
  {
    id:          'medical',
    label:       'Medical',
    icon:        '🏥',
    desc:        'Hospitals, urgent care, and medical resources',
    layers:      [],
    defaultZoom: 11,
  },
  {
    id:          'power',
    label:       'Power Outages',
    icon:        '⚡',
    desc:        'Power outages — Entergy data + community reports',
    layers:      [],
    defaultZoom: 11,
  },
  {
    id:          'connectivity',
    label:       'Connectivity',
    icon:        '📶',
    desc:        'WiFi, charging, Starlink, and LTE recovery zones',
    layers:      [],
    defaultZoom: 12,
  },
];

// ── Marker color palette ──────────────────────────────────────────
export const MARKER_COLORS = {
  // By incident type
  sos:           '#7f1d1d',
  flood:         '#1e40af',
  road_flooding: '#dc2626',
  road_closure:  '#ea580c',
  power_outage:  '#7c3aed',
  fire:          '#dc2626',
  accident:      '#d97706',
  hazmat:        '#dc2626',
  // By shelter status
  shelter_open:        '#059669',
  shelter_at_capacity: '#d97706',
  shelter_closed:      '#6b7280',
  // By gas status
  gas_open:   '#059669',
  gas_busy:   '#d97706',
  gas_closed: '#6b7280',
  // By flood zone
  zone_VE:   '#7c3aed',
  zone_AE:   '#1d4ed8',
  zone_X500: '#d97706',
  zone_X:    '#059669',
  // User location
  user:    '#0369a1',
  // Fallback
  default: '#6b7280',
  // Connectivity
  wifi_public:   '#0369a1',
  wifi_shelter:  '#059669',
  charging:      '#7c3aed',
  starlink:      '#1d4ed8',
  open_business: '#d97706',
  lte_recovery:  '#dc2626',
  // Connectivity status
  conn_active:   '#059669',
  conn_likely:   '#d97706',
  conn_offline:  '#dc2626',
  conn_unknown:  '#6b7280',
};

// ── Evac route styles ─────────────────────────────────────────────
export const EVAC_ROUTE_STYLE = {
  open:       { color: '#059669', weight: 5, opacity: 0.85, dashArray: null },
  caution:    { color: '#d97706', weight: 5, opacity: 0.85, dashArray: '10,6' },
  closed:     { color: '#dc2626', weight: 4, opacity: 0.7,  dashArray: '6,6' },
  contraflow: { color: '#7c3aed', weight: 6, opacity: 0.9,  dashArray: null },
  unknown:    { color: '#6b7280', weight: 3, opacity: 0.5,  dashArray: '4,8' },
};

// ── Attribution strings ───────────────────────────────────────────
export const ATTRIBUTION = {
  leaflet:    '<a href="https://leafletjs.com">Leaflet</a>',
  nws:        '<a href="https://weather.gov">NWS</a>',
  fema:       '<a href="https://msc.fema.gov">FEMA</a>',
  usgs:       '<a href="https://waterdata.usgs.gov">USGS</a>',
  osm:        '© <a href="https://openstreetmap.org">OSM</a>',
};
